import { cn } from "cn";
import { Mic, X } from "lucide-react";
import { defineOrbTheme, Orb } from "orb-ui";
import { createOpenAILiveAdapter, type OpenAILiveEvent, type OpenAILiveOrbAdapter } from "orb-ui/adapters";
import { useEffect, useRef, useState } from "react";
import { ApiError, api } from "../../api";
import { EMPTY_TRANSCRIPT, foldTranscript, transcriptText } from "../../lib/voice-transcript";
import { BRAND } from "../../pixel/palette";
import { Button } from "../ui/button";
import { Dialog, DialogContent, DialogDescription, DialogTitle } from "../ui/dialog";

export type VoiceDraft = {
  title: string;
  description: string;
  typeKey: string;
  priority: number;
};

type Phase = "connecting" | "listening" | "finishing" | "writing" | "creating" | "error";

const STATUS: Record<Exclude<Phase, "error">, string> = {
  connecting: "Connecting",
  listening: "Listening",
  finishing: "Finishing the transcript",
  writing: "Writing the issue",
  creating: "Creating the issue",
};

/**
 * Radial orb in the Leaselab brand. Maroon and salmon carry the pinwheel:
 * dark lobes bloom from maroon into salmon, light lobes stay in the salmon tints.
 * Playback stays muted: this is dictation.
 */
const voiceTheme = defineOrbTheme({
  name: "radial",
  preset: "expressive",
  appearance: {
    deepColor: BRAND.maroon,
    cobaltColor: BRAND.salmon,
    aquaColor: BRAND.salmon50,
    paleColor: BRAND.salmon20,
    membraneColor: BRAND.salmon,
    seamColor: BRAND.white,
    idleControlColor: BRAND.maroon,
    activeControlColor: BRAND.salmon,
    connectingControlColor: BRAND.maroon50,
  },
});

export function VoiceIssueButton({
  disabled,
  onOpenChange,
  onCreate,
}: {
  disabled: boolean;
  onOpenChange?: (open: boolean) => void;
  onCreate: (draft: VoiceDraft) => Promise<void>;
}) {
  const [open, setOpen] = useState(false);
  const startRef = useRef<(() => void) | null>(null);

  return (
    <>
      <Button
        type="button"
        size="sm"
        variant="outline"
        disabled={disabled}
        onClick={() => {
          setOpen(true);
          onOpenChange?.(true);
          startRef.current?.();
        }}
      >
        <Mic />
        Voice
      </Button>
      <VoiceIssueDialog
        open={open}
        startRef={startRef}
        onOpenChange={(next) => {
          setOpen(next);
          onOpenChange?.(next);
        }}
        onCreate={onCreate}
      />
    </>
  );
}

function VoiceIssueDialog({
  open,
  startRef,
  onOpenChange,
  onCreate,
}: {
  open: boolean;
  startRef: { current: (() => void) | null };
  onOpenChange: (open: boolean) => void;
  onCreate: (draft: VoiceDraft) => Promise<void>;
}) {
  const [phase, setPhase] = useState<Phase>("connecting");
  const [transcript, setTranscript] = useState("");
  const [message, setMessage] = useState("");
  const [adapter, setAdapter] = useState<OpenAILiveOrbAdapter | null>(null);
  const adapterRef = useRef<OpenAILiveOrbAdapter | null>(null);
  const generation = useRef(0);
  const mounted = useRef(true);
  const transcriptRef = useRef("");
  const scrollRef = useRef<HTMLParagraphElement>(null);
  transcriptRef.current = transcript;

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
      generation.current += 1;
      const current = adapterRef.current;
      adapterRef.current = null;
      void current?.stop().catch(() => undefined);
    };
  }, []);

  useEffect(() => {
    const node = scrollRef.current;
    if (!node || !transcript) return;
    node.scrollTop = node.scrollHeight;
  }, [transcript]);

  function close() {
    generation.current += 1;
    const current = adapterRef.current;
    adapterRef.current = null;
    setAdapter(null);
    void current?.stop().catch(() => undefined);
    onOpenChange(false);
  }

  function begin() {
    const gen = ++generation.current;
    const previous = adapterRef.current;
    adapterRef.current = null;
    void previous?.stop().catch(() => undefined);
    const spoken = { current: EMPTY_TRANSCRIPT };
    const next = createOpenAILiveAdapter({
      createSession: (sdp, signal) => api.voiceSession(sdp, signal),
      onEvent: (event) => {
        if (!mounted.current || generation.current !== gen) return;
        if (event.type === "session.input_transcript.delta" && typeof event.delta === "string") {
          spoken.current = foldTranscript(spoken.current, { type: event.type, delta: event.delta });
          const text = transcriptText(spoken.current);
          transcriptRef.current = text;
          setTranscript(text);
          return;
        }
        if (event.type === "error") {
          setMessage(eventMessage(event));
          setPhase("error");
        }
      },
      createAudioElement: () => {
        const audio = new Audio();
        audio.muted = true;
        return audio;
      },
    });
    adapterRef.current = next;
    setAdapter(next);
    setTranscript("");
    transcriptRef.current = "";
    setMessage("");
    setPhase("connecting");
    void next.start().then(
      () => {
        if (!mounted.current || generation.current !== gen) return;
        setPhase("listening");
      },
      (error: unknown) => {
        if (!mounted.current || generation.current !== gen || cancelled(error)) return;
        setMessage(readable(error));
        setPhase("error");
      },
    );
  }
  startRef.current = begin;

  async function create() {
    if (phase === "connecting" || phase === "finishing" || phase === "writing" || phase === "creating")
      return;
    if (phase === "error" && !transcriptRef.current.trim()) {
      begin();
      return;
    }
    const gen = generation.current;
    const current = adapterRef.current;
    if (current) {
      setPhase("finishing");
      adapterRef.current = null;
      try {
        await current.stop();
      } catch (error) {
        if (!mounted.current || generation.current !== gen || cancelled(error)) return;
      }
      if (!mounted.current || generation.current !== gen) return;
    }
    const spoken = transcriptRef.current.trim();
    setAdapter(null);
    if (!spoken) {
      setMessage("I didn't catch that. Try again.");
      setPhase("error");
      return;
    }
    setPhase("writing");
    try {
      const draft = await api.voiceDraft(spoken);
      if (!mounted.current || generation.current !== gen) return;
      setPhase("creating");
      await onCreate(draft);
      if (!mounted.current || generation.current !== gen) return;
      close();
    } catch (error) {
      if (!mounted.current || generation.current !== gen) return;
      setMessage(error instanceof ApiError ? error.message : "Could not create the issue.");
      setPhase("error");
    }
  }

  const busy = phase === "connecting" || phase === "finishing" || phase === "writing" || phase === "creating";
  const primary = primaryLabel(phase, transcript);
  const status = phase === "error" ? message : STATUS[phase];
  const live = phase === "listening" || phase === "finishing";

  return (
    <Dialog open={open} onOpenChange={(next) => !next && close()}>
      {/* The shared dialog zooms in from 95%. This sheet covers the page at full size. */}
      <DialogContent
        sheet={false}
        showCloseButton={false}
        style={{ animation: "none" }}
        className="inset-0 top-0 left-0 flex h-dvh max-h-none w-screen max-w-none translate-x-0 translate-y-0 flex-col gap-0 rounded-none bg-background p-0 pt-[env(safe-area-inset-top)] pb-[env(safe-area-inset-bottom)] shadow-none ring-0 sm:max-w-none"
      >
        <div className="flex items-start justify-between gap-4 px-6 pt-6">
          <div className="min-w-0">
            <DialogTitle className="text-sm font-medium">Describe the issue</DialogTitle>
            <DialogDescription className={cn("mt-1", phase === "error" && "text-destructive")}>
              {status}
            </DialogDescription>
          </div>
          <Button type="button" variant="ghost" size="icon" onClick={close}>
            <X />
            <span className="sr-only">Close</span>
          </Button>
        </div>

        <div className="flex min-h-0 flex-1 flex-col items-center justify-center gap-10 px-6">
          {adapter ? (
            <Orb adapter={adapter} theme={voiceTheme} size={280} interactive={false} />
          ) : (
            <Orb signal={waitingSignal(phase)} theme={voiceTheme} size={280} interactive={false} />
          )}
          <p
            ref={scrollRef}
            aria-live="polite"
            className={cn(
              "max-h-48 w-full max-w-xl overflow-y-auto text-center text-xl leading-snug font-medium tracking-[-0.02em] text-balance",
              transcript ? "text-foreground" : "text-muted-foreground",
            )}
          >
            {transcript || "Start talking. Your words show up here."}
          </p>
          {(live || transcript) && (
            <p className="text-sm text-muted-foreground">Press create when you are done talking.</p>
          )}
        </div>

        <div className="flex items-center justify-center gap-2 px-6 pb-10">
          <Button type="button" variant="outline" onClick={close} disabled={phase === "creating"}>
            Cancel
          </Button>
          <Button type="button" onClick={() => void create()} disabled={busy}>
            {primary}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function waitingSignal(phase: Phase): { state: "connecting" | "thinking" | "error"; inputVolume: number } {
  if (phase === "error") return { state: "error", inputVolume: 0 };
  if (phase === "writing" || phase === "creating") return { state: "thinking", inputVolume: 0 };
  return { state: "connecting", inputVolume: 0 };
}

function primaryLabel(phase: Phase, transcript: string): string {
  if (phase === "finishing") return "Transcribing…";
  if (phase === "writing" || phase === "creating") return "Creating…";
  if (phase === "error" && !transcript.trim()) return "Try again";
  return "Create issue";
}

function eventMessage(event: OpenAILiveEvent): string {
  const error = event.error;
  if (error instanceof Error && error.message) return readable(error);
  if (error && typeof error === "object" && "message" in error && typeof error.message === "string") {
    return error.message;
  }
  if (typeof event.message === "string") return event.message;
  return "Voice transcription failed";
}

function readable(error: unknown): string {
  if (error instanceof ApiError) return error.message;
  const message = error instanceof Error ? error.message : "Could not start the microphone.";
  return message.replace(/^\[orb-ui\/openai-live\]\s*/, "");
}

function cancelled(error: unknown): boolean {
  if (error instanceof DOMException && error.name === "AbortError") return true;
  const message = error instanceof Error ? error.message : "";
  return /cancelled/i.test(message);
}
